import { ProductDocument } from '@util/types/firestore/products';

// FeaturedListings' chip-filter logic, kept free of UI imports so it stays
// unit-testable (jest can't transform lucide-react's ESM through the
// component). Product→card mapping lives in productCardProps.ts.

export type Filter =
    | { key: string; label: string; kind: 'all' }
    | { key: string; label: string; kind: 'category'; slug: string }
    | { key: string; label: string; kind: 'maxPrice'; maxPrice: number };

// Category slugs match CategoryNav's level-1 slugs.
export const FILTERS: Filter[] = [
    { key: 'all', label: 'All', kind: 'all' },
    { key: 'cameras', label: 'Cameras', kind: 'category', slug: 'digital-cameras' },
    { key: 'lenses', label: 'Lenses', kind: 'category', slug: 'photo-video-lenses' },
    { key: 'cinema', label: 'Cinema', kind: 'category', slug: 'video-camcorders' },
    { key: 'drones', label: 'Drones', kind: 'category', slug: 'drones-aerial-imaging' },
    { key: 'lighting', label: 'Lighting', kind: 'category', slug: 'lighting-studio' },
    { key: 'under-500', label: 'Under $500', kind: 'maxPrice', maxPrice: 500 },
];

/**
 * Applies a chip to the listing set.
 * `nameBySlug` is null until the slug→category-name lookup settles; callers show
 * skeletons for category chips until then rather than calling this with null.
 */
export function applyFilter(
    items: ProductDocument[],
    filter: Filter | undefined,
    nameBySlug: Record<string, string> | null
): ProductDocument[] {
    if (!filter || filter.kind === 'all') return items;
    if (filter.kind === 'maxPrice') {
        return items.filter((i) => i.price < filter.maxPrice);
    }
    const name = nameBySlug?.[filter.slug];
    // Lookup settled but this slug never resolved — show everything rather than
    // an empty grid caused by a failed category fetch.
    if (!name) return items;
    return items.filter((i) => i.categories?.includes(name));
}
